import _ from 'lodash'
import moment from 'moment'
import React from 'react'

import { FLAG_COLOURS } from 'constants/colors'
import { DISPLAY_SHORT_DATE_FORMAT } from 'constants/date'
import { BasicViewProps } from 'constants/models'
import {
  BEFORE_AFTER_EATING_LABELS,
  FREQUENCY_LABELS,
  MEDICATION_FORM_STATUSES,
  MEDICATION_FORM_STATUS_LABELS,
  MF_SIGNATURE_TYPES,
} from 'services/medicationForms/constants'
import { MedicationForm } from 'services/medicationForms/models'

import { convertTimeDuration, floatToHoursAndMinutes } from 'utils/date'
import { generateRoute } from 'utils/routing'

import { Avatar, Banner, Callout, DateString, DetailsBreakdown, Page, Section, Spinner, Typography } from 'components'
import { SignaturePreview } from 'module/Safeguarding/components'
import PreviewStatus from 'module/Safeguarding/components/PreviewStatus'

import i18n from 'translations'

import ReviewForm, { AddReviewFormVales, STATUS } from './components/ReviewForm'
import { MEDICATION_FORM_STATUS_COLORS } from './constants'
import { StyledActions, StyledSignatureValueContainer } from './PreviewStyle'

interface PreviewViewProps extends BasicViewProps {
  childId?: string
  initialValues: AddReviewFormVales
  isLongTerm: boolean
  medicationForm?: MedicationForm
  onCancel: () => void
  queryStatus?: string
}

const AddView: React.FC<PreviewViewProps> = ({
  childId,
  errorMessages,
  initialValues,
  isLoading,
  isLongTerm,
  isSubmitting,
  medicationForm,
  onCancel,
  onSubmit,
  queryStatus,
}) => {
  if (isLoading || !medicationForm) {
    return <Spinner />
  }

  const {
    child,
    endDate,
    id,
    medicationDetails,
    nextReviewDate,
    otherNotes,
    signatures,
    startDate,
    status,
  } = medicationForm
  const {
    beforeOrAfterEating,
    dose,
    expiryDate,
    frequency,
    howLongBeforeOrAfterEating,
    medication,
    methodOfAdministration,
    reason,
    sideEffects,
    storageInstructions,
    storeOnSite,
    timesOfAdministration,
  } = medicationDetails

  const parent = _.find(signatures, { type: MF_SIGNATURE_TYPES.PARENT })
  const staff = _.find(signatures, { type: MF_SIGNATURE_TYPES.STAFF })
  const remainingExpirationWeeks = moment(expiryDate)
    .startOf('day')
    .diff(moment().startOf('day'), 'weeks', true)
  const isMedicationExpiring = status === MEDICATION_FORM_STATUSES.ACTIVE
    && 2 >= remainingExpirationWeeks

  const renderBreakdownItem = ({ label, padding = '0 0 15px 0', value }) => (
    <DetailsBreakdown.Item label={label} margin="0" padding={padding} value={value || '-'} withoutBorder />
  )

  const renderHoursAndMinutes = () => {
    const { hours, minutes } = floatToHoursAndMinutes(convertTimeDuration(
      +howLongBeforeOrAfterEating,
      'milliseconds',
      'hours',
    ))

    let hoursAndMinutes = ''

    if (hours) {
      hoursAndMinutes += i18n.t('global:Hours', { value: hours })
    }

    if (minutes) {
      hoursAndMinutes += (hoursAndMinutes ? ' ' : '') + i18n.t('global:Minutes', { value: minutes })
    }

    return hoursAndMinutes
  }

  const renderMedicationDetails = () => (
    <React.Fragment>
      <Typography fontSize={20} margin="15px 0" bold>
        {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:title')}
      </Typography>
      <DetailsBreakdown>
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:medication'),
          value: medication,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:reason'),
          value: reason,
        })}
        {renderBreakdownItem({
          label: i18n.t(
            'module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:methodOfAdministration',
          ),
          value: methodOfAdministration,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:dose'),
          value: dose,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:frequency'),
          value: FREQUENCY_LABELS[frequency],
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:administrationTime'),
          value: _.join(_.map(timesOfAdministration, (time) => moment(time).utc().format('HH:mm')), ', '),
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:beforeAfterEating'),
          value: BEFORE_AFTER_EATING_LABELS[beforeOrAfterEating],
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:beforeAfterEatingTime'),
          value: howLongBeforeOrAfterEating ? renderHoursAndMinutes() : null,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:expiryDate'),
          value: expiryDate ? (
            <Typography
              color={(status === MEDICATION_FORM_STATUSES.EXPIRED || isMedicationExpiring) && FLAG_COLOURS.ERROR}
            >
              {moment(expiryDate).format(DISPLAY_SHORT_DATE_FORMAT)}
            </Typography>
          ) : null,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:sideEffects'),
          value: sideEffects,
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:MedicationDetails:notes'),
          value: otherNotes,
        })}
      </DetailsBreakdown>
    </React.Fragment>
  )

  const renderStockDetails = () => (
    <React.Fragment>
      <Typography fontSize={20} margin="15px 0" bold>
        {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Stock:title')}
      </Typography>
      <DetailsBreakdown>
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Stock:storeOnSite'),
          value: storeOnSite ? i18n.t('global:Yes') : i18n.t('global:No'),
        })}
        {renderBreakdownItem({
          label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Stock:storageInstructions'),
          value: storageInstructions,
        })}
      </DetailsBreakdown>
    </React.Fragment>
  )

  const renderSignatureDetails = () => (
    <React.Fragment>
      <Typography fontSize={20} margin="15px 0" bold>
        {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:title')}
      </Typography>
      {staff && (
        <React.Fragment>
          <Typography fontSize={20} margin="15px 0" bold>
            {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Staff:title')}
          </Typography>
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Staff:administratedBy'),
            padding: '0',
            value: (
              <StyledSignatureValueContainer>
                <div>
                  <Avatar
                    avatarSize="small"
                    initials={staff.owner.name.split(' ')}
                    src={staff.owner.photo}
                    title={staff.owner.name}
                  />
                </div>
                <SignaturePreview signedAt={staff.signedAt} url={staff.signature.url} />
              </StyledSignatureValueContainer>
            ),
          })}
        </React.Fragment>
      )}
      {parent?.carer ? (
        <React.Fragment>
          <Typography fontSize={20} margin="15px 0" bold>
            {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Parent:title')}
          </Typography>
          {renderBreakdownItem({
            label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Parent:parent'),
            padding: '0',
            value: (
              <StyledSignatureValueContainer>
                <div>
                  <Avatar
                    avatarSize="small"
                    initials={[parent.carer.firstName, parent.carer.surname]}
                    src={parent.carer.photo}
                    title={`${parent.carer.firstName} ${parent.carer.surname}`}
                  />
                </div>
                <SignaturePreview signedAt={parent.signedAt} url={parent.signature.url} />
              </StyledSignatureValueContainer>
            ),
          })}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  )

  const actions = (
    <StyledActions>
      <Typography margin="0 10px 0 0" variant="div">
        <PreviewStatus
          badgeColor={MEDICATION_FORM_STATUS_COLORS[status]}
          statusLabel={MEDICATION_FORM_STATUS_LABELS[status]}
        />
      </Typography>
      <Section.Actions
        dropdownMinWidth={300}
        options={[
          {
            icon: 'medication-form-history',
            label: i18n.t('module:Safeguarding:MedicationForm:Preview:History:title'),
            to: childId
              ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.HISTORY', { childId, id })
              : generateRoute('SAFEGUARDING.MEDICATION_FORM.HISTORY', { id }),
          }, {
            auth: status !== MEDICATION_FORM_STATUSES.PAST && status !== MEDICATION_FORM_STATUSES.UPCOMING,
            icon: 'refresh',
            label: i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:reviewMedicationForm'),
            to: childId
              ? `${generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', { childId, id })}?status=review`
              : `${generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id })}?status=review`,
          },
        ]}
      />
    </StyledActions>
  )

  return (
    <Page.Section actions={actions} title={medication}>
      {status === MEDICATION_FORM_STATUSES.EXPIRED && (
        <Banner.Error margin="0 0 15px">
          {i18n.t('module:Safeguarding:MedicationForm:Preview:expiryNotes')}
        </Banner.Error>
      )}
      <Callout content={errorMessages} error />
      <DetailsBreakdown>
        <DetailsBreakdown.Item center={false} margin="0" padding="0 0 15px 0" withoutBorder>
          <Avatar
            avatarSize="medium_big"
            initials={[child.firstName, child.surname]}
            src={child.photo}
            title={(
              <Typography fontSize={18} bold>
                {child.firstName}
                {' '}
                {child.surname}
              </Typography>
            )}
          />
        </DetailsBreakdown.Item>
        {renderBreakdownItem({
          label: _.upperFirst(i18n.t('global:startDate')),
          value: startDate ? moment(startDate).format(DISPLAY_SHORT_DATE_FORMAT) : null,
        })}
        {renderBreakdownItem({
          label: _.upperFirst(i18n.t('global:endDate')),
          value: endDate ? <DateString date={endDate} local={false} /> : null,
        })}
        {isLongTerm && (
          renderBreakdownItem({
            label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:reviewDate'),
            value: nextReviewDate
              ? moment(nextReviewDate).format(DISPLAY_SHORT_DATE_FORMAT)
              : null,
          })
        )}
      </DetailsBreakdown>
      {renderMedicationDetails()}
      {renderStockDetails()}
      {renderSignatureDetails()}
      {queryStatus === STATUS
        && status !== MEDICATION_FORM_STATUSES.DRAFT
        && status !== MEDICATION_FORM_STATUSES.PAST
        && (
          <ReviewForm
            initialValues={initialValues}
            isLongTerm={isLongTerm}
            isSubmitting={isSubmitting}
            medicationForm={medicationForm}
            queryStatus={queryStatus}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        )}
    </Page.Section>
  )
}

export default AddView
